/**
 * Resolve pricing country (must match Laravel Country.name).
 * Priority on server: VPN/browser public IP (query + headers) → geo APIs → fallback.
 * When user sent a browser IP for geo but lookup fails: do NOT use device timezone —
 * PCs in BD often stay on Asia/Dhaka while VPN exits abroad.
 */

/** Intl "CZ" → DB uses "Czech Republic", etc. */
const REGION_TO_DB_COUNTRY = {
    CZ: 'Czech Republic',
    CI: 'Ivory Coast',
    TR: 'Turkey',
}

/** ISO 3166-1 alpha-2 → common currency code for display prefix */
const REGION_TO_CURRENCY = {
    BD: 'BDT',
    US: 'USD',
    GB: 'GBP',
    IN: 'INR',
    PK: 'PKR',
    SA: 'SAR',
    AE: 'AED',
    SG: 'SGD',
    MY: 'MYR',
    ID: 'IDR',
    TH: 'THB',
    VN: 'VND',
    PH: 'PHP',
    JP: 'JPY',
    CN: 'CNY',
    KR: 'KRW',
    AU: 'AUD',
    NZ: 'NZD',
    CA: 'CAD',
    MX: 'MXN',
    BR: 'BRL',
    DE: 'EUR',
    FR: 'EUR',
    IT: 'EUR',
    ES: 'EUR',
    NL: 'EUR',
    BE: 'EUR',
    AT: 'EUR',
    IE: 'EUR',
    PT: 'EUR',
    SE: 'SEK',
    NO: 'NOK',
    DK: 'DKK',
    CH: 'CHF',
    PL: 'PLN',
    TR: 'TRY',
    HK: 'HKD',
    EG: 'EGP',
    ZA: 'ZAR',
    NG: 'NGN',
    KE: 'KES',
    AF: 'AFN',
}

/** When Accept-Language has no region (e.g. only "en"), map IANA zone → region code */
const TIMEZONE_TO_REGION = {
    'Asia/Dhaka': 'BD',
    'Asia/Dacca': 'BD',
    'Asia/Kolkata': 'IN',
    'Asia/Karachi': 'PK',
    'Asia/Riyadh': 'SA',
    'Asia/Dubai': 'AE',
    'Asia/Singapore': 'SG',
    'Asia/Kuala_Lumpur': 'MY',
    'Asia/Jakarta': 'ID',
    'Asia/Bangkok': 'TH',
    'Asia/Ho_Chi_Minh': 'VN',
    'Asia/Manila': 'PH',
    'Asia/Tokyo': 'JP',
    'Asia/Shanghai': 'CN',
    'Asia/Hong_Kong': 'HK',
    'Asia/Seoul': 'KR',
    'Australia/Sydney': 'AU',
    'Australia/Melbourne': 'AU',
    'Pacific/Auckland': 'NZ',
    'America/New_York': 'US',
    'America/Chicago': 'US',
    'America/Denver': 'US',
    'America/Los_Angeles': 'US',
    'America/Toronto': 'CA',
    'America/Vancouver': 'CA',
    'America/Mexico_City': 'MX',
    'America/Sao_Paulo': 'BR',
    'Europe/London': 'GB',
    'Europe/Paris': 'FR',
    'Europe/Berlin': 'DE',
    'Europe/Rome': 'IT',
    'Europe/Madrid': 'ES',
    'Europe/Amsterdam': 'NL',
    'Europe/Brussels': 'BE',
    'Europe/Vienna': 'AT',
    'Europe/Dublin': 'IE',
    'Europe/Lisbon': 'PT',
    'Europe/Stockholm': 'SE',
    'Europe/Oslo': 'NO',
    'Europe/Copenhagen': 'DK',
    'Europe/Zurich': 'CH',
    'Europe/Warsaw': 'PL',
    'Europe/Istanbul': 'TR',
    'Africa/Cairo': 'EG',
    'Africa/Johannesburg': 'ZA',
    'Africa/Lagos': 'NG',
    'Africa/Nairobi': 'KE',
    'Asia/Kabul': 'AF',
}

export function regionFromAcceptLanguage(acceptLanguage) {
    if (!acceptLanguage || typeof acceptLanguage !== 'string') return null
    const items = acceptLanguage.split(',')
    for (const item of items) {
        const tag = item.split(';')[0].trim()
        if (!tag) continue
        const segments = tag.split('-')
        if (segments.length >= 2) {
            const last = segments[segments.length - 1]
            if (last.length === 2 && /^[A-Za-z]{2}$/.test(last)) {
                return last.toUpperCase()
            }
        }
    }
    return null
}

export function regionFromTimeZone(timeZone) {
    if (!timeZone || typeof timeZone !== 'string') return null
    return TIMEZONE_TO_REGION[timeZone] || null
}

export function regionToCountryName(region) {
    if (!region) return null
    const upper = region.toUpperCase()
    if (REGION_TO_DB_COUNTRY[upper]) {
        return REGION_TO_DB_COUNTRY[upper]
    }
    try {
        if (typeof Intl !== 'undefined' && Intl.DisplayNames) {
            const dn = new Intl.DisplayNames(['en'], { type: 'region' })
            const name = dn.of(upper)
            if (name && name !== upper) return name
        }
    } catch {
        // ignore
    }
    return null
}

export function currencyForRegion(region) {
    if (!region) return ''
    return REGION_TO_CURRENCY[region.toUpperCase()] || ''
}

/**
 * Extract browser/client IP from Next.js API request (trust proxy headers on VPS).
 */
export function getClientIpFromRequest(req) {
    const xff = req.headers['x-forwarded-for']
    if (typeof xff === 'string' && xff.length > 0) {
        const first = xff.split(',')[0].trim()
        if (first) return normalizeIpString(first)
    }
    const realIp = req.headers['x-real-ip']
    if (typeof realIp === 'string' && realIp.trim()) {
        return normalizeIpString(realIp.trim())
    }
    const raw = req.socket?.remoteAddress || ''
    return normalizeIpString(raw)
}

function normalizeIpString(ip) {
    if (!ip) return ''
    if (ip.startsWith('::ffff:')) return ip.slice(7)
    return ip.trim()
}

/** Skip geo lookup for localhost / RFC1918 (dev & LAN). */
export function isNonPublicIp(ip) {
    if (!ip || typeof ip !== 'string') return true
    const i = ip.trim().toLowerCase()
    if (i === '127.0.0.1' || i === '::1' || i === 'localhost') return true
    if (i.startsWith('10.')) return true
    if (i.startsWith('192.168.')) return true
    if (i.startsWith('169.254.')) return true
    if (i.startsWith('172.')) {
        const parts = i.split('.')
        if (parts.length >= 2) {
            const n = parseInt(parts[1], 10)
            if (!Number.isNaN(n) && n >= 16 && n <= 31) return true
        }
    }
    return false
}

/** Accept browser-reported IP only if it looks publicly routable (anti-spoof for LAN). */
export function isPublicRoutableIp(ip) {
    if (!ip || typeof ip !== 'string') return false
    const n = normalizeIpString(ip.trim())
    if (!n) return false
    if (isNonPublicIp(n)) return false
    if (n.includes(':')) {
        const lower = n.toLowerCase()
        if (lower === '::1') return false
        if (lower.startsWith('fe80:')) return false
        if (lower.startsWith('fc') || lower.startsWith('fd')) return false
        return true
    }
    const parts = n.split('.')
    if (parts.length !== 4) return false
    for (const p of parts) {
        const num = parseInt(p, 10)
        if (Number.isNaN(num) || num < 0 || num > 255) return false
    }
    return true
}

/**
 * For pricing/VPN: prefer browser-reported public IP (ipify in the client — follows VPN)
 * over X-Forwarded-For. Otherwise production nginx always sends the user's real country IP
 * and UK VPN would still show Bangladesh.
 * Query ?bip= survives CDNs that strip non-standard headers.
 */
export function getEffectiveClientIp(req) {
    const qip = typeof req.query?.bip === 'string' ? req.query.bip.trim() : ''
    if (qip) {
        const normalized = normalizeIpString(qip)
        if (normalized && isPublicRoutableIp(normalized)) {
            return normalized
        }
    }
    const hint = req.headers['x-browser-public-ip']
    if (typeof hint === 'string') {
        const normalized = normalizeIpString(hint.trim())
        if (normalized && isPublicRoutableIp(normalized)) {
            return normalized
        }
    }
    return getClientIpFromRequest(req)
}

async function geoFromIpwho(ip, signal) {
    const res = await fetch(`https://ipwho.is/${encodeURIComponent(ip)}`, {
        signal,
        headers: { Accept: 'application/json' },
    })
    if (!res.ok) return null
    const data = await res.json()
    if (!data.success) return null
    const code = (data.country_code || '').toUpperCase()
    if (!code) return null
    const countryName =
        REGION_TO_DB_COUNTRY[code] ||
        regionToCountryName(code) ||
        (typeof data.country === 'string' ? data.country : null)
    if (!countryName) return null
    const currency =
        (data.currency && data.currency.code) ||
        currencyForRegion(code) ||
        ''
    return { countryName, currency, region: code }
}

async function geoFromIpApi(ip, signal) {
    const url = `http://ip-api.com/json/${encodeURIComponent(ip)}?fields=status,country,countryCode,currency`
    const res = await fetch(url, { signal })
    if (!res.ok) return null
    const data = await res.json()
    if (data.status !== 'success') return null
    const code = (data.countryCode || '').toUpperCase()
    if (!code) return null
    const countryName =
        REGION_TO_DB_COUNTRY[code] ||
        regionToCountryName(code) ||
        (typeof data.country === 'string' ? data.country : null)
    if (!countryName) return null
    const currency = data.currency || currencyForRegion(code) || ''
    return { countryName, currency, region: code }
}

async function lookupCountryFromIpGeo(ip) {
    const run = async fetcher => {
        const controller = new AbortController()
        const timeout = setTimeout(() => controller.abort(), 8500)
        try {
            return await fetcher(ip, controller.signal)
        } catch {
            return null
        } finally {
            clearTimeout(timeout)
        }
    }
    let out = await run(geoFromIpwho)
    if (out) return out
    out = await run(geoFromIpApi)
    return out
}

/**
 * IP geo first (VPN exit). If user sent browser IP (?bip / header) but geo fails,
 * do not fall back to timezone (Asia/Dhaka on PC while VPN exits abroad).
 */
export async function resolvePricingLocaleAsync(input) {
    const clientIp = input.clientIp || ''
    const acceptLanguage = input.acceptLanguage || ''
    const timeZone = input.timeZone || ''
    const omitTimezoneFallback = Boolean(input.omitTimezoneFallback)

    if (clientIp && !isNonPublicIp(clientIp)) {
        const fromIp = await lookupCountryFromIpGeo(clientIp)
        if (fromIp) return fromIp
        if (omitTimezoneFallback) {
            return resolvePricingLocale({
                acceptLanguage,
                timeZone: '',
                omitTimezoneFallback: true,
            })
        }
    }

    return resolvePricingLocale({ acceptLanguage, timeZone, omitTimezoneFallback: false })
}

/**
 * Call from the browser: hits /api/pricing-locale so Accept-Language is applied.
 */
async function fetchBrowserPublicIp() {
    const urls = [
        'https://api.ipify.org?format=json',
        'https://api64.ipify.org?format=json',
    ]
    for (const url of urls) {
        try {
            const controller = new AbortController()
            const t = setTimeout(() => controller.abort(), 6500)
            const ipRes = await fetch(url, { signal: controller.signal })
            clearTimeout(t)
            if (!ipRes.ok) continue
            const j = await ipRes.json()
            if (typeof j.ip === 'string' && j.ip.trim()) return j.ip.trim()
        } catch {
            // try next URL
        }
    }
    return ''
}

export async function fetchPricingLocaleClient() {
    if (typeof window === 'undefined') {
        return resolvePricingLocale({ acceptLanguage: '', timeZone: '' })
    }
    let tz = ''
    try {
        tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
    } catch {
        tz = ''
    }

    const browserIp = await fetchBrowserPublicIp()

    const headers = {
        'x-client-timezone': tz,
    }
    if (browserIp) {
        headers['x-browser-public-ip'] = browserIp
    }

    const qs =
        browserIp !== ''
            ? `?bip=${encodeURIComponent(browserIp)}`
            : ''
    const r = await fetch(`/api/pricing-locale${qs}`, { headers })
    if (!r.ok) {
        return resolvePricingLocale({
            acceptLanguage:
                typeof navigator !== 'undefined' ? navigator.language : '',
            timeZone: tz,
            omitTimezoneFallback: browserIp !== '',
        })
    }
    const data = await r.json()
    return data
}

export function resolvePricingLocale(input) {
    const acceptLanguage = input.acceptLanguage || ''
    const timeZone = input.timeZone || ''
    const omitTimezoneFallback = Boolean(input.omitTimezoneFallback)

    // With VPN: never use device timezone when omitTimezoneFallback — it stays Asia/Dhaka.
    let region = null
    if (!omitTimezoneFallback) {
        region = regionFromTimeZone(timeZone)
    }
    if (!region) {
        region = regionFromAcceptLanguage(acceptLanguage)
    }

    if (!region) {
        return {
            countryName: 'Afghanistan',
            currency: '',
            region: null,
        }
    }

    const countryName = regionToCountryName(region) || 'Afghanistan'
    const currency = currencyForRegion(region)

    return {
        countryName,
        currency,
        region,
    }
}
